/**
 * Tailwind class resolution for WBS Gantt bars, keyed by item status with
 * a derived "overdue" override. Centralised here (rather than inline in
 * `WbsGantt`) so the colour map is a single, unit-testable source of truth.
 *
 * The status palette mirrors the badge tones in `WbsTreeTable` but uses
 * deeper fills — a bar carries more surface area than a pill, so the
 * lighter badge tones would wash out against the row tint.
 *
 * "Overdue" is a derived flag, not a stored status. When true it overrides
 * the status palette with red. `classifyDelay` (the only producer of this
 * flag) already excludes the terminal statuses `done`/`cancelled`, so an
 * overdue bar is always one of not_started / in_progress / blocked — the
 * override never fires on a completed or cancelled row.
 *
 * Note: the project's `cancelled` status is the "skip" case (中止) — it
 * renders muted with a diagonal stripe so a deliberately-dropped row reads
 * differently from an untouched not_started bar.
 */
import type { WbsItemStatus } from '@mct/shared';

export interface WbsBarColors {
  /** Background-fill class for the bar body. */
  bg: string;
  /** Border-colour class. */
  border: string;
  /** Text-colour class for the in-bar label. */
  text: string;
  /**
   * When true the bar gets the `.bg-stripe` diagonal overlay (cancelled /
   * skipped rows). Kept as a flag rather than folded into `bg` because the
   * stripe is a background-image that layers on top of the `bg` colour.
   */
  striped: boolean;
}

const STATUS_COLORS: Record<WbsItemStatus, WbsBarColors> = {
  not_started: {
    bg: 'bg-white',
    border: 'border-slate-300',
    text: 'text-slate-700',
    striped: false,
  },
  in_progress: {
    bg: 'bg-blue-300',
    border: 'border-blue-500',
    text: 'text-blue-800',
    striped: false,
  },
  done: {
    bg: 'bg-slate-200',
    border: 'border-slate-400',
    text: 'text-slate-600',
    striped: false,
  },
  blocked: {
    bg: 'bg-red-400',
    border: 'border-red-600',
    text: 'text-white',
    striped: false,
  },
  cancelled: {
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-400',
    striped: true,
  },
};

const OVERDUE_COLORS: WbsBarColors = {
  bg: 'bg-red-300',
  border: 'border-red-500',
  text: 'text-red-800',
  striped: false,
};

/**
 * Resolve the bar colours for a row. `isOverdue` wins over `status`: a
 * delayed row always renders in the overdue red regardless of its
 * underlying status.
 */
export function getWbsBarColors(
  status: WbsItemStatus,
  isOverdue: boolean,
): WbsBarColors {
  if (isOverdue) return OVERDUE_COLORS;
  return STATUS_COLORS[status];
}
